/**
 * Exportación y restauración de respaldos contra la base de datos (Req. 6).
 *
 * En un iPhone los datos locales pueden desaparecer por varias vías: borrar
 * los datos de navegación, borrar el ícono, perder el teléfono. Sin nube, el
 * archivo de respaldo es la ÚNICA red de seguridad; por eso está en la Fase 0.
 *
 * La validación del formato vive en backup-format.ts, que es código puro y
 * tiene pruebas. Aquí solo está lo que lee y escribe en IndexedDB.
 *
 * Origen: Control de Negocio de Comida. Cambios: verificación del archivo
 * antes de entregarlo, copia interna en una base separada en lugar de una
 * descarga automática, y "Deshacer restauración".
 */

import { db, settingsIniciales } from './schema';
import { calcularChecksum } from './checksum';
import { nuevoId } from './ids';
import { enEscritura, enLectura, mensajeDeError } from './tx';
import { guardarCopia, leerCopia, type BaseCopias, type MotivoCopia } from './copias';
import { ahoraIso } from '@/domain/dates';
import { ErrorRespaldo } from '@/domain/errors';
import { TABLAS, VERSION_ESQUEMA, escribirTabla, type Bytes, type MapaTablas, type NombreTabla, type Settings } from './types';
import { asegurarCatalogoBase } from './ejercicios';
import {
  IDENTIFICADOR_APP,
  FORMATO_VERSION,
  analizarRespaldo,
  calcularRangoFechas,
  nombreArchivoRespaldo,
  registrosCapturados,
  serializarRespaldo,
  tablasVacias,
  validarRegistros,
  verificarRespaldoGenerado,
  type ArchivoRespaldo,
  type RespaldoAnalizado,
} from './backup-format';
import {
  armarZip,
  conFotosDeclaradas,
  fotosQueEntran,
  verificarZipGenerado,
} from './backup-zip';
import { textoDeProblemas } from '@/domain/fotos';

export * from './backup-format';
export * from './backup-zip';

/**
 * Los bytes verificados que salieron del ZIP, como Blob para guardarlos en
 * `photoBlobs`. Van con su tipo: así la galería los muestra sin adivinar.
 */
export function fotosComoBlobs(fotos: ReadonlyMap<string, Bytes>): Map<string, Blob> {
  const blobs = new Map<string, Blob>();
  for (const [id, bytes] of fotos) blobs.set(id, new Blob([bytes], { type: 'image/jpeg' }));
  return blobs;
}

function modoDeEjecucion(): string {
  if (typeof window === 'undefined') return 'desconocido';
  const enStandalone =
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return enStandalone ? 'standalone' : 'navegador';
}

// ---------------------------------------------------------------------------
// Exportación
// ---------------------------------------------------------------------------

/**
 * Lee todas las tablas en UNA transacción de lectura, para obtener una foto
 * consistente, y arma el archivo (Req. 6.1). Los conteos salen de la misma
 * transacción: son los "conteos de la base" con que se verifica el archivo.
 */
/**
 * Un respaldo en JSON NO declara las fotos de progreso: no puede llevar sus
 * bytes, y declararlas prometería fotos que el archivo no trae (design.md
 * 10.7). Las declara solo el respaldo con fotos, con `conFotosDeclaradas`.
 *
 * Vale igual para las copias internas, que también son JSON: por eso
 * restaurar una copia no toca las fotos del teléfono.
 */
export async function exportarRespaldo(): Promise<ArchivoRespaldo> {
  const data = tablasVacias();
  const counts: Record<string, number> = {};

  await enLectura(db.tables, async () => {
    for (const tabla of TABLAS) {
      const omitir = tabla === 'photos';
      const registros = omitir ? [] : await db.table(tabla).toArray();
      escribirTabla(data, tabla, registros);
      // El conteo sale del índice, no del arreglo: si `toArray` dejara un
      // registro fuera, la verificación del archivo lo detectaría.
      counts[tabla] = omitir ? 0 : await db.table(tabla).count();
    }
  });

  const ajustes = data.settings[0];
  return {
    app: IDENTIFICADOR_APP,
    formatVersion: FORMATO_VERSION,
    schemaVersion: VERSION_ESQUEMA,
    exportedAt: ahoraIso(),
    device: modoDeEjecucion(),
    perfil: { id: ajustes?.perfilId ?? null, nombre: ajustes?.nombre ?? null },
    counts,
    dateRange: calcularRangoFechas(data),
    checksum: await calcularChecksum(data),
    data,
  };
}

export interface RespaldoPreparado {
  archivo: ArchivoRespaldo;
  /** El texto ya verificado: lo que se descarga, se comparte o se copia. */
  texto: string;
  nombre: string;
  totalRegistros: number;
  /** Datos raros encontrados al verificar. No impiden entregarlo. */
  avisos: string[];
}

/**
 * Genera el respaldo y lo VERIFICA antes de entregarlo (Req. 6.13): lo vuelve
 * a leer como si se fuera a restaurar y compara la suma y los conteos con los
 * de la base. Si algo no cuadra lanza y no hay nada que entregar.
 */
export async function prepararRespaldo(fecha = new Date()): Promise<RespaldoPreparado> {
  const archivo = await exportarRespaldo();
  const texto = serializarRespaldo(archivo);
  const { resumen } = await verificarRespaldoGenerado(texto, archivo.counts);
  return {
    archivo,
    texto,
    nombre: nombreArchivoRespaldo(fecha),
    totalRegistros: resumen.totalRegistros,
    avisos: [...resumen.errores, ...resumen.advertencias],
  };
}

export interface RespaldoConFotosPreparado {
  archivo: ArchivoRespaldo;
  /** El `respaldo.json` de dentro: el mismo texto del respaldo sin fotos. */
  texto: string;
  /** El ZIP ya verificado: lo que se descarga o se comparte. */
  zip: Bytes;
  nombre: string;
  totalRegistros: number;
  /** Cuántas fotos van en el archivo. */
  fotos: number;
  avisos: string[];
}

/**
 * Genera el respaldo CON fotos y lo VERIFICA antes de entregarlo (Req. 6.13,
 * 6.17; design.md 10.7):
 *
 * 1. Lee los datos de las fotos y sus bytes, y compara cada uno con la huella
 *    que se guardó al tomarlos: una foto dañada no entra y se avisa.
 * 2. Exporta el respaldo de siempre y le agrega las fotos que sí entran, con
 *    su suma de verificación al día.
 * 3. Arma el ZIP y lo vuelve a abrir como si fuera un archivo elegido para
 *    restaurar: el JSON idéntico, la suma válida, los conteos de la base y la
 *    huella de cada foto. Si algo no cuadra, no hay nada que entregar.
 */
export async function prepararRespaldoConFotos(
  fecha = new Date(),
): Promise<RespaldoConFotosPreparado> {
  const { declaradas, blobs } = await enLectura([db.photos, db.photoBlobs], async () => {
    const datos = await db.photos.toArray();
    const encontrados = new Map<string, Blob>();
    for (const foto of datos) {
      const guardado = await db.photoBlobs.get(foto.id);
      if (guardado !== undefined) encontrados.set(foto.id, guardado.datos);
    }
    return { declaradas: datos, blobs: encontrados };
  });

  // Los Blob se pasan a bytes FUERA de la transacción: esperar una promesa
  // que no es de Dexie dentro de una la cerraría antes de tiempo.
  const bytesPorId = new Map<string, Bytes>();
  for (const [id, blob] of blobs) bytesPorId.set(id, new Uint8Array(await blob.arrayBuffer()));

  const { buenas, problemas } = await fotosQueEntran(declaradas, bytesPorId);
  const archivo = await conFotosDeclaradas(
    await exportarRespaldo(),
    declaradas.filter((f) => buenas.has(f.id)),
  );
  const texto = serializarRespaldo(archivo);
  const { resumen } = await verificarRespaldoGenerado(texto, archivo.counts);

  const zip = armarZip(texto, buenas, archivo.exportedAt);
  await verificarZipGenerado(zip, { texto, archivo });

  const deLasFotos = textoDeProblemas(problemas);
  return {
    archivo,
    texto,
    zip,
    nombre: nombreArchivoRespaldo(fecha, false, true),
    totalRegistros: resumen.totalRegistros,
    fotos: buenas.size,
    avisos: [
      ...resumen.errores,
      ...resumen.advertencias,
      ...(deLasFotos === null ? [] : [`De las fotos de progreso: ${deLasFotos}.`]),
    ],
  };
}

/**
 * Registra el respaldo hecho. Solo se llama cuando la descarga o el compartir
 * terminaron bien; copiar el texto no cuenta (Req. 6.4). `momento` es la
 * fecha de exportación del archivo: los datos respaldados son los de ese
 * instante.
 */
export async function registrarRespaldoHecho(momento = ahoraIso()): Promise<void> {
  await enEscritura([db.settings], async () => {
    const existentes = await db.settings.get(1);
    if (existentes === undefined) {
      await db.settings.put({ ...settingsIniciales(), ultimoRespaldoEn: momento });
    } else {
      await db.settings.update(1, { ultimoRespaldoEn: momento });
    }
  });
}

// ---------------------------------------------------------------------------
// Restauración (design.md, 10.2)
// ---------------------------------------------------------------------------

export interface OpcionesRestauracion {
  /**
   * Motivo de la copia de los datos actuales que se guarda antes de
   * reemplazarlos (Req. 6.10). Restaurar guarda "antes_de_restaurar";
   * deshacer guarda "antes_de_deshacer", para no reemplazar la copia que está
   * restaurando y para que deshacer tampoco pierda nada.
   */
  motivoCopia?: MotivoCopia;
  /** Base de copias; se cambia en las pruebas. */
  copias?: BaseCopias;
  /**
   * Los bytes de las fotos verificadas, cuando el archivo es un ZIP. Sin
   * ellos las fotos del teléfono se quedan como están (ver OpcionesReemplazo).
   */
  fotos?: ReadonlyMap<string, Blob>;
}

export interface ResultadoRestauracion {
  /** Registros escritos, sin contar los ajustes. */
  registros: number;
  /** Motivo de la copia guardada antes de reemplazar. */
  copia: MotivoCopia;
}

/** El instante más reciente de dos, o el que sea válido. */
export function masReciente(
  a: string | null | undefined,
  b: string | null | undefined,
): string | null {
  const ta = typeof a === 'string' ? Date.parse(a) : Number.NaN;
  const tb = typeof b === 'string' ? Date.parse(b) : Number.NaN;
  if (Number.isNaN(ta)) return Number.isNaN(tb) ? null : (b ?? null);
  if (Number.isNaN(tb)) return a ?? null;
  return ta >= tb ? (a ?? null) : (b ?? null);
}

/**
 * Reemplaza TODOS los datos en una sola transacción (Req. 6.9, 6.14): borra
 * cada tabla y escribe la del respaldo. Si cualquier escritura falla,
 * IndexedDB revierte la transacción completa y los datos quedan como estaban.
 *
 * No valida: quien llama ya validó en memoria. Se exporta para probar que un
 * fallo a la mitad no cambia nada.
 */
export interface OpcionesReemplazo {
  respaldoEn?: string | null;
  /**
   * Los bytes de las fotos que trae el archivo, cuando es un ZIP (10.7).
   *
   * Si no viene, las fotos del teléfono NO se tocan: un respaldo en JSON no
   * las trae y tampoco es prueba de que no hubiera ninguna, así que borrarlas
   * sería destruir lo que ese archivo nunca prometió reemplazar. Es la única
   * excepción a "restaurar reemplaza y no combina" (Req. 6.9), y existe
   * porque la regla de la casa es que nada se borra solo.
   */
  fotos?: ReadonlyMap<string, Blob>;
}

export async function reemplazarDatos(
  datos: MapaTablas,
  opciones: OpcionesReemplazo = {},
): Promise<void> {
  const fotos = opciones.fotos;
  await enEscritura(db.tables, async () => {
    const delTelefono = await db.settings.get(1);

    for (const tabla of TABLAS) {
      if (tabla === 'photos' && fotos === undefined) continue;
      await db.table(tabla).clear();
    }
    if (fotos !== undefined) await db.photoBlobs.clear();

    for (const tabla of TABLAS) {
      if (tabla === 'settings') continue;
      if (tabla === 'photos' && fotos === undefined) continue;
      // Ausente es vacía: un respaldo de un esquema anterior no trae las tablas nuevas.
      const registros = (datos[tabla] ?? []) as readonly unknown[];
      if (registros.length > 0) await db.table(tabla).bulkPut(registros as unknown[]);
    }
    if (fotos !== undefined) {
      for (const [id, bytes] of fotos) await db.photoBlobs.put({ id, datos: bytes });
    }

    // Ajustes: los del archivo (ya normalizados al analizarlo) o, si no trae,
    // los de este teléfono.
    const delArchivo = datos.settings?.[0];
    const base: Settings = delArchivo ?? delTelefono ?? settingsIniciales();
    await db.settings.put({
      ...base,
      id: 1,
      // Un archivo sin identificador de perfil conserva el del teléfono.
      perfilId: base.perfilId !== '' ? base.perfilId : (delTelefono?.perfilId || nuevoId('per')),
      schemaVersion: VERSION_ESQUEMA,
      // Es del teléfono, no de los datos: se vuelve a detectar al arrancar.
      almacenamientoPersistente: null,
      // El cifrado configurado en ESTE teléfono se respeta: restaurar un
      // respaldo viejo no lo apaga ni trae de vuelta una contraseña anterior.
      // Un teléfono sin cifrado toma el del respaldo (por ejemplo, al reinstalar).
      cifradoRespaldo: delTelefono?.cifradoRespaldo ?? base.cifradoRespaldo ?? null,
      // El archivo restaurado existe desde su fecha de exportación: esa es la
      // fecha del último respaldo de estos datos.
      ultimoRespaldoEn: masReciente(base.ultimoRespaldoEn, opciones.respaldoEn),
    });
  });
}

/**
 * Restaura un respaldo ya analizado y confirmado por el usuario.
 *
 * 1. Valida todos los registros en memoria (ya migrados) antes de tocar nada.
 * 2. Guarda la copia interna de los datos actuales y la verifica; si falla,
 *    no se continúa (Req. 6.10).
 * 3. Reemplaza todo en una transacción (Req. 6.9, 6.14).
 *
 * La validación va antes de la copia para no reemplazar la copia de una
 * restauración anterior por culpa de un archivo que de todos modos se rechaza.
 */
export async function restaurarRespaldo(
  analizado: RespaldoAnalizado,
  opciones: OpcionesRestauracion = {},
): Promise<ResultadoRestauracion> {
  const { bloqueantes } = validarRegistros(analizado.datos);
  if (bloqueantes.length > 0) {
    throw new ErrorRespaldo(
      `El respaldo tiene registros dañados y no se puede restaurar: ${bloqueantes.join(' ')} ` +
        'No se modificó ningún dato.',
    );
  }

  const motivo = opciones.motivoCopia ?? 'antes_de_restaurar';
  try {
    await guardarCopia(motivo, await exportarRespaldo(), opciones.copias);
  } catch (error) {
    throw new ErrorRespaldo(
      `No se pudo guardar la copia de tus datos actuales (${mensajeDeError(error)}). Por ` +
        'seguridad no se restauró nada y tus datos siguen como estaban.',
    );
  }

  await reemplazarDatos(analizado.datos, {
    // Al deshacer no hay archivo nuevo: se conserva la fecha que tenía la copia.
    respaldoEn: motivo === 'antes_de_restaurar' ? analizado.archivo.exportedAt : null,
    ...(opciones.fotos !== undefined ? { fotos: opciones.fotos } : {}),
  });
  // Un respaldo anterior a la Fase 2 no trae el catálogo base: se vuelve a
  // agregar sin tocar los ejercicios que sí traía (design.md, 10.2, paso 11).
  await asegurarCatalogoBase();

  return {
    registros: registrosCapturados(analizado.datos as unknown as Record<NombreTabla, readonly unknown[]>),
    copia: motivo,
  };
}

/**
 * "Deshacer restauración" (Req. 6.10, 6.11): vuelve a los datos que había
 * antes de la última restauración, por el mismo flujo.
 *
 * Antes guarda los datos actuales como copia "antes_de_deshacer": lo que se
 * haya registrado después de restaurar no se pierde, y la copia que se está
 * restaurando se conserva hasta la siguiente restauración.
 */
export async function deshacerRestauracion(copias?: BaseCopias): Promise<ResultadoRestauracion> {
  const copia = await leerCopia('antes_de_restaurar', copias);
  if (copia === undefined) {
    throw new ErrorRespaldo('No hay una restauración que deshacer.');
  }
  const analizado = await analizarRespaldo(copia.contenido);
  if (!analizado.resumen.checksumValido) {
    throw new ErrorRespaldo(
      'La copia interna no pasó la verificación y no se usó. Tus datos siguen como estaban.',
    );
  }
  return restaurarRespaldo(analizado, { motivoCopia: 'antes_de_deshacer', copias });
}
